import Vue from 'vue';
import Vuex from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import account, { types as accountTypes } from './modules/account';
import cart, {
  getters as cartGetters,
  types as cartTypes,
} from './modules/cart';
import categories, {
  getters as categoriesGetters,
  types as categoriesTypes,
} from './modules/categories';
import header, {
  getters as headerGetters,
  types as headerTypes,
} from './modules/header';
import home, { types as homeTypes } from './modules/home';
import menu, { types as menuTypes } from './modules/menu';
import navigation, { types as navigationTypes } from './modules/navigation';
import product, { types as productTypes } from './modules/product';
import productList, {
  getters as productListGetters,
  types as productListTypes,
} from './modules/productList';
import productCustomizer, {
  getters as productCustomizerGetters,
  types as productCustomizerTypes,
} from './modules/productCustomizer';
import search, {
  getters as searchGetters,
  types as searchTypes,
} from './modules/search';
import createPersistence from '../plugins/vuex-session';

Vue.use(Vuex);

/**
 * An object grouping all namespaced Vuex action and mutation types
 * by module. Allows strong typing and IDE autocompletion instead of
 * raw string literals.
 *
 * @namespace types
 * @property {Object} account - Actions/mutations for the account module.
 * @property {Object} cart - Actions/mutations for the cart module.
 * @property {Object} categories - Actions/mutations for the categories module.
 * @property {Object} header - Actions/mutations for the header module.
 * @property {Object} home - Actions/mutations for the home module.
 * @property {Object} menu - Actions/mutations for the menu module.
 * @property {Object} navigation - Actions/mutations for the navigation module.
 * @property {Object} product - Actions/mutations for the product module.
 * @property {Object} productList - Actions/mutations for the productList module.
 * @property {Object} productCustomizer - Actions/mutations for the productCustomizer module.
 * @property {Object} search - Actions/mutations for the search module.
 */
export const types = {
  account: accountTypes,
  cart: cartTypes,
  categories: categoriesTypes,
  header: headerTypes,
  home: homeTypes,
  menu: menuTypes,
  navigation: navigationTypes,
  product: productTypes,
  productList: productListTypes,
  productCustomizer: productCustomizerTypes,
  search: searchTypes,
};

/**
 * The root Vuex store configuration, including modules, getters,
 * mutations, and plugins.
 */
export const storeOptions = {
  modules: {
    account,
    cart,
    categories,
    header,
    home,
    menu,
    navigation,
    product,
    productList,
    productCustomizer,
    search,
  },

  getters: {
    /**
     * Retrieves the addToCartUrl from the loaded product details.
     * @param {Object} state - The Vuex root state.
     * @returns {string} The product add-to-cart URL.
     */
    addToCartUrl: (state) => state.product.details.addToCartUrl,

    /**
     * Returns a deep clone of the product data for a given ID.
     * Prevents accidental mutations of store state.
     * @param {Object} state - The Vuex root state.
     * @returns {function(string): Object} Returns a function that
     * receives a product ID and retrieves the product.
     */
    availableProductDataInPLP: (state) => (productId) => {
      const found = state.productList.products
        .flatMap((c) => c.items)
        .find(
          (e) =>
            e.product?.id === productId ||
            e.product?.banner?.productSlider?.some((s) => s.id === productId)
        );

      const item = found?.product.banner?.productSlider
        ? found.product.banner.productSlider.find((s) => s.id === productId)
        : found;

      return cloneDeep(item || {});
    },

    /**
     * Extracts state subset for session persistence.
     * @param {Object} state - The Vuex root state.
     * @returns {Object} The persisted UI-related state.
     */
    getStateForSession: (state) => ({
      columnMode: state.productList.ui.columnMode,
    }),
  },

  mutations: {
    /**
     * Restores persisted UI settings from session.
     * @param {Object} state - The Vuex root state to mutate.
     * @param {Object} backup - Partial state from session.
     * @returns {void}
     */
    rehydrateFromSession: (state, backup) => {
      if (backup?.columnMode) {
        // eslint-disable-next-line no-param-reassign
        state.productList.ui.columnMode = backup.columnMode;
      }
    },
  },

  plugins: [
    createPersistence({
      shouldPersist: (mutationType) =>
        productListTypes.setColumnMode === mutationType,
    }),
  ],
};

/**
 * Shorthand mapping of root-level getters to module getters.
 *
 * @namespace getters
 * @property {Object} categories - Getters from categories module.
 * @property {Object} cart - Getters from cart module.
 * @property {Object} header - Getters from header module.
 * @property {Object} productCustomizer - Getters from productCustomizer module.
 * @property {Object} productList - Getters from productList module.
 * @property {Object} search - Getters from search module.
 */
export const getters = {
  categories: categoriesGetters,
  cart: cartGetters,
  header: headerGetters,
  productCustomizer: productCustomizerGetters,
  productList: productListGetters,
  search: searchGetters,
};

export default new Vuex.Store(storeOptions);
